import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ SUPABASE CONFIG ERROR: Missing required environment variables');
  console.error('Required: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Always use production URL for auth redirects
const PRODUCTION_URL = 'https://leadgenpr6367.builtwithrocket.new';

export const getSiteUrl = () => PRODUCTION_URL;

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        redirectTo: `${PRODUCTION_URL}/email-confirmation`,
        storageKey: 'leadgen-pro-auth',
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        debug: false,
      },
      global: {
        headers: {
          'X-Client-Info': 'leadgen-pro-react'
        },
        fetch: (url, options = {}) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller?.abort(), 15000);
          return fetch(url, { ...options, signal: controller?.signal })?.finally(() => clearTimeout(timeoutId));
        }
      }
    })
  : null;

// Test actual connectivity to Supabase
export const testSupabaseConnection = async () => {
  if (!supabaseUrl) {
    return { ok: false, reason: 'missing_config' };
  }
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller?.abort(), 8000);
    const res = await fetch(`${supabaseUrl}/auth/v1/health`, {
      method: 'GET',
      signal: controller?.signal,
      headers: { 'apikey': supabaseAnonKey || '' }
    })?.finally(() => clearTimeout(timeoutId));
    if (res?.ok || res?.status === 200 || res?.status === 401) {
      return { ok: true };
    }
    return { ok: false, reason: 'server_error', status: res?.status };
  } catch (err) {
    if (err?.name === 'AbortError') {
      return { ok: false, reason: 'timeout' };
    }
    return { ok: false, reason: 'network', message: err?.message };
  }
};

export const handleAuthRedirect = async () => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return null;
    }
    const { data, error } = await supabase?.auth?.getSession();
    if (error) {
      console.error('Auth redirect error:', error);
      return null;
    }
    return data?.session;
  } catch (error) {
    console.error('Error handling auth redirect:', error);
    return null;
  }
};

export const signUpWithEmail = async (email, password, options = {}) => {
  try {
    if (!supabase) {
      return { data: null, error: { message: 'Supabase client not initialized' } };
    }
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        ...options,
        emailRedirectTo: `${PRODUCTION_URL}/email-confirmation`
      }
    });
    if (!error && data?.user && !data?.session) {
      try { localStorage?.setItem('pendingConfirmationEmail', email); } catch (e) {}
    }
    return { data, error };
  } catch (error) {
    console.error('Error in signUpWithEmail:', error);
    return { data: null, error };
  }
};

export const isAuthenticated = async () => {
  try {
    if (!supabase) return false;
    const { data: { session } } = await supabase?.auth?.getSession();
    return !!session?.user;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export const getCurrentUser = async () => {
  try {
    if (!supabase) return null;
    const { data: { session } } = await supabase?.auth?.getSession();
    return session?.user || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const getUserProfile = async (userId) => {
  try {
    if (!supabase) return null;
    if (!userId) {
      const user = await getCurrentUser();
      if (!user) return null;
      userId = user?.id;
    }
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.maybeSingle();
    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    return null;
  }
};

export const handleSupabaseError = (error) => {
  if (!error) return null;
  const msg = error?.message || '';
  const lowerMsg = msg?.toLowerCase();
  if (
    lowerMsg?.includes('failed to fetch') ||
    lowerMsg?.includes('authretryablefetcherror') ||
    lowerMsg?.includes('networkerror') ||
    lowerMsg?.includes('network request failed') ||
    error?.name === 'AuthRetryableFetchError'
  ) {
    return 'Unable to connect to the authentication server. The service may be temporarily unavailable.';
  }
  if (lowerMsg?.includes('invalid login credentials') || lowerMsg?.includes('invalid credentials')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }
  if (lowerMsg?.includes('email not confirmed')) {
    return 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
  }
  if (lowerMsg?.includes('user already registered')) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  if (lowerMsg?.includes('violates')) {
    return 'This information is already in use. Please try different details.';
  }
  return msg || 'An unexpected error occurred. Please try again.';
};

export const checkSupabaseConnection = () => {
  if (!supabaseUrl || !supabaseAnonKey) {
    return {
      isConnected: false,
      error: 'Missing Supabase configuration. Please check your environment variables.',
      details: { hasUrl: !!supabaseUrl, hasAnonKey: !!supabaseAnonKey }
    };
  }
  return {
    isConnected: true,
    error: null,
    details: { url: supabaseUrl, hasAnonKey: !!supabaseAnonKey }
  };
};

export default supabase;